import {
  useEffect, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import { getSelectedSiteFilterValue, getStateName } from 'src/utils/getInitials';
import Loader from 'src/common/Loader';
import { getDriverList, loadMoreDrivers } from 'src/redux/driverSlice';
import { useDispatch, useSelector } from 'react-redux';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { userTableHeaders, getAlignByColumnId } from 'src/utils/tableHeaders';
import { TABLE_ROWS_PER_PAGE } from 'src/utils/constants';

const DriverListTable = ({
  refreshData, searchText, siteFilter, ...rest
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { loading, driverList, totalCount } = useSelector((state) => state.drivers);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('first_name');
  const userAlignById = useMemo(() => getAlignByColumnId(userTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(getDriverList({
      limit,
      pageNo: 1,
      params: { user__first_name: searchText, site: selectedSite }
    }));
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const loadMoreData = (currentPage, newLimit) => {
    const newPage = currentPage + 1;
    if (totalCount > driverList.length) {
      dispatch(loadMoreDrivers({
        limit: newLimit,
        pageNo: newPage,
        params: { user__first_name: searchText, site: selectedSite }
      }));
    }
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
    dispatch(getDriverList({
      limit: newLimit,
      params: { user__first_name: searchText, site: selectedSite }
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    loadMoreData(newPage, limit);
  };

  const viewDriverInfo = (driverId) => {
    history.push(`${ROUTE.drivers}/${driverId}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Card {...rest}>
          <PerfectScrollbar>
            <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
              <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={userTableHeaders}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {driverList
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .slice(page * limit, page * limit + limit)
                    .map((user) => (
                      <TableRow hover key={user.id}>
                        <TableCell align={userAlignById.first_name}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex'
                            }}
                          >
                            <Typography color="textPrimary" variant="body1" noWrap title={`${user.first_name} ${user.last_name}`}>
                              {`${user.first_name} ${user.last_name}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align={userAlignById.emp_id}>{user.emp_id}</TableCell>
                        <TableCell align={userAlignById.mobile_num}>{user.mobile_num}</TableCell>
                        <TableCell align={userAlignById.city}>
                          <Typography noWrap title={`${user.city}, ${getStateName(user.state)}`}>
                            {`${user.city}, ${getStateName(user.state)}`}
                          </Typography>
                        </TableCell>
                        <TableCell align={userAlignById.timezone}>
                          <Typography noWrap title={user.timezone || 'Asia/Kolkata'}>
                            {user.timezone || 'Asia/Kolkata'}
                          </Typography>
                        </TableCell>
                        <TableCell align={userAlignById.is_active}>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align={userAlignById.action}>
                          <Button
                            variant="contained"
                            onClick={() => viewDriverInfo(user.id)}
                            size="small"
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>
          <TablePagination
            component="div"
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
          />
        </Card>
      )}
    </>
  );
};

DriverListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default DriverListTable;
